/**
 * Permission-based UI components
 * Conditionally render children based on user permissions
 */

import { ReactNode } from 'react';
import { useAuth } from '@/providers/auth.provider';
import { hasPermission, hasAllPermissions, hasAnyPermission } from '@/lib/permissions';
import { Permission } from '@/constants/permissions.constants';

interface PermissionGuardProps {
  children: ReactNode;
  permission: Permission;
  fallback?: ReactNode;
}

interface AllPermissionsGuardProps {
  children: ReactNode;
  permissions: Permission[];
  fallback?: ReactNode;
}

interface AnyPermissionGuardProps {
  children: ReactNode;
  permissions: Permission[];
  fallback?: ReactNode;
}

/**
 * Renders children only if user has the required permission
 */
export function PermissionGuard({ children, permission, fallback = null }: PermissionGuardProps) {
  const { user } = useAuth();
  
  if (!hasPermission(user, permission)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

/**
 * Renders children only if user has ALL specified permissions
 */
export function AllPermissionsGuard({ children, permissions, fallback = null }: AllPermissionsGuardProps) {
  const { user } = useAuth();
  
  if (!hasAllPermissions(user, permissions)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

/**
 * Renders children only if user has ANY of the specified permissions
 */
export function AnyPermissionGuard({ children, permissions, fallback = null }: AnyPermissionGuardProps) {
  const { user } = useAuth();
  
  if (!hasAnyPermission(user, permissions)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

/**
 * Renders children only if user is admin
 */
export function AdminGuard({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { user } = useAuth();
  
  if (user?.role?.name !== 'Admin') {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}
